import React, { useState } from 'react';
import './LandingPageStyles.css';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import websiteLogo from '../../assets/images/websiteLogo-160.png';
import { 
    FaCar, 
    FaUserTie, 
    FaBell, 
    FaFileAlt, 
    FaChartBar, 
    FaShieldAlt, 
    FaPhoneAlt, 
    FaEnvelope, 
    FaWhatsapp, 
    FaCheckCircle, 
    FaArrowLeft, 
    FaLaptop, 
    FaMobileAlt, 
    FaCloud, 
    FaLock,
    FaArrowRight,
    FaCalendarCheck,
    FaCogs,
    FaChevronDown,
    FaQuestionCircle
} from 'react-icons/fa';

const FAQ_ITEMS = [
    {
        q: 'מהי מערכת דבורה וכיצד היא מייעלת את ניהול צי הרכב?',
        a: 'מערכת דבורה היא מערכת ענן מקיפה ומתקדמת שפותחה במיוחד עבור חברות, מנהלי ציים וקציני בטיחות בתעבורה בישראל. המערכת מרכזת את כל תיקי הרכב, תיקי הנהגים, מועדי טסטים, ביטוחים, טיפולים, תקלות, דוחות תנועה והדרכות בטיחות במקום אחד מאובטח ונגיש מכל מכשיר 24/7.'
    },
    {
        q: 'מהם היתרונות של מערכת דבורה ניהול צי רכב עבור קציני בטיחות בתעבורה?',
        a: 'מערכת דבורה ניהול צי רכב תוכננה בקפידה עבור קציני בטיחות ומנהלי ציים. היא מאפשרת מעקב שוטף ובקרה הדוקה, התראות אוטומטיות לפני פקיעת תוקף מסמכים, החתמת נהגים על טופסי בדיקת רכב והדרכות בטיחות במסך מגע, והפקת דוחות רשמיים מוכנים לביקורת משרד התחבורה בלחיצת כפתור.'
    },
    {
        q: 'האם מערכת דבורה מתאימה גם לחברות בעלות צי רכבים קטן או בינוני?',
        a: 'בהחלט. מערכת דבורה בנויה במבנה גמיש ומודולרי המתאים בצורה מושלמת החל מציים של רכבים בודדים ועד ציי ענק של מאות כלי רכב, תוך חיסכון מוכח של עשרות שעות אדמיניסטרטיביות ומניעת קנסות והשבתות.'
    },
    {
        q: 'האם מערכת דבורה ניהול צי רכב דורשת התקנת תוכנה או שרתים?',
        a: 'לא, מערכת דבורה פועלת במודל SaaS (ענן מלא). אין צורך בהתקנה פיזית על המחשב, אין צורך בתחזוקת שרתים, וכל העדכונים והגיבויים מתבצעים אוטומטית. ניתן להתחבר מכל דפדפן, מחשב, טאבלט או סמארטפון בצורה מאובטחת ומוצפנת.'
    },
    {
        q: 'כיצד מתבצעות הדרכות נהגים ובדיקות תקינות במערכת דבורה?',
        a: 'המערכת מאפשרת הפקת קישור מותאם הנשלח לנהג ישירות ב-WhatsApp או SMS. הנהג עובר על הדרכת הבטיחות השנתית מהסמארטפון, ממלא את טופס הבדיקה, וחותם דיגיטלית ישירות על מסך המגע. כל הנתונים והאישורים מתועדים מיידית בתיק הרכב ובתיק הנהג ללא כל צורך בניירת.'
    },
    {
        q: 'האם ניתן להתנסות במערכת דבורה ללא עלות?',
        a: 'כן! תקבלו תקופת ניסיון מלאה בחינם כי כרגע אנו מעניקים את השימוש במערכת דבורה בחינם לגמרי לעוד הרבה זמן, ללא שום עלות התקנה, ללא הגבלה וללא התחייבות.'
    }
];

const EMAIL_BACKEND_URL =
    process.env.REACT_APP_EMAIL_SERVER_URL ||
    process.env.REACT_APP_BACKEND_URL ||
    'https://us-central1-carwebsite-6f5cb.cloudfunctions.net/api';

const LandingPage = () => {
    const navigate = useNavigate();
    const [activeRoleTab, setActiveRoleTab] = useState('officer'); // 'officer' | 'manager' | 'driver'
    const [openFaqIndices, setOpenFaqIndices] = useState([0]); // First FAQ item open by default
    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        fleetSize: '1-10',
        notes: ''
    });
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const toggleFaq = (index) => {
        setOpenFaqIndices(prev => 
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError('');

        const leadPayload = {
            fullName: (formData.fullName || '').trim(),
            phone: (formData.phone || '').trim(),
            fleetSize: formData.fleetSize,
            notes: (formData.notes || '').trim(),
            createdAt: new Date().toISOString(),
            source: 'homepage_lead_form'
        };

        try {
            // 1. Store lead in Firestore backup
            try {
                const { default: firebase } = await import('../../services/firebase-config');
                if (firebase && firebase.firestore) {
                    await firebase.firestore().collection('leads').add({
                        ...leadPayload,
                        status: 'new',
                        timestamp: firebase.firestore.FieldValue.serverTimestamp()
                    });
                }
            } catch (dbErr) {
                console.warn('Could not save lead to Firestore:', dbErr);
            }

            // 2. Send email notification via backend
            const response = await fetch(`${EMAIL_BACKEND_URL}/send-contact-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(leadPayload)
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                console.error('Email send failed on server:', errData);
            }

            setFormSubmitted(true);
            setFormData({
                fullName: '',
                phone: '',
                fleetSize: '1-10',
                notes: ''
            });
        } catch (err) {
            console.error('Error submitting form:', err);
            setSubmitError('אירעה שגיאה בשליחת הפנייה. אנא נסה שוב או צור קשר ישירות בטלפון.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const landingPageSchema = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "SoftwareApplication",
                "name": "מערכת דבורה",
                "alternateName": [
                    "דבורה ניהול צי רכב",
                    "מערכת דבורה",
                    "דבורה ציי רכב",
                    "דבורה קצין בטיחות בתעבורה",
                    "Dvora Fleet"
                ],
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Web",
                "description": "נמאס לכם מטבלאות אקסל? מערכת דבורה מנהלת עבורכם את צי הרכב: בקרת הוצאות דלק וליסינג, התראות על טסטים וביטוחים, תיק רכב דיגיטלי וחיסכון מוכח. תאמו הדגמה קצרה",
                "offers": {
                    "@type": "Offer",
                    "price": "0",
                    "priceCurrency": "ILS",
                    "description": "תקבלו תקופת ניסיון מלאה בחינם – כרגע המערכת ניתנת בחינם לגמרי לעוד הרבה זמן ללא התחייבות"
                }
            },
            {
                "@type": "FAQPage",
                "mainEntity": FAQ_ITEMS.map(item => ({
                    "@type": "Question",
                    "name": item.q,
                    "acceptedAnswer": {
                        "@type": "Answer",
                        "text": item.a
                    }
                }))
            }
        ]
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="landing-page-root">
            <SEO 
                title="מערכת דבורה – ניהול צי רכב וקציני בטיחות בתעבורה"
                description="נמאס לכם מטבלאות אקסל? מערכת דבורה מנהלת עבורכם את צי הרכב: בקרת הוצאות דלק וליסינג, התראות על טסטים וביטוחים, תיק רכב דיגיטלי וחיסכון מוכח. תאמו הדגמה קצרה"
                keywords="מערכת דבורה, דבורה ניהול צי רכב, מערכת דבורה, דבורה ציי רכב, דבורה קצין בטיחות בתעבורה, תוכנה לניהול צי רכב דבורה, ניהול צי רכב, קצין בטיחות בתעבורה, תיק רכב דיגיטלי, תיק נהג, בדיקת רכב"
                canonical="https://dvorafleet.co.il/"
                ogImage="https://dvorafleet.co.il/logo512.png"
                schema={landingPageSchema}
            />

            {/* ── 1. Sticky Navigation Header ── */}
            <header className="landing-header">
                <div className="landing-container header-container">
                    <div className="landing-brand-group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <img src={websiteLogo} alt="מערכת דבורה - ניהול צי רכב" className="landing-brand-logo-img" width="160" height="50" />
                        <div className="brand-text">
                            <span className="brand-name">דבורה</span>
                            <span className="brand-sub">מערכת לניהול צי רכב וקציני בטיחות בתעבורה</span>
                        </div>
                    </div>

                    <nav className="landing-nav-links">
                        <a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }} className="lp-nav-link-btn">יתרונות המערכת</a>
                        <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }} className="lp-nav-link-btn">איך זה עובד</a>
                        <a href="#devices" onClick={(e) => { e.preventDefault(); scrollToSection('devices'); }} className="lp-nav-link-btn">זמינות וטפסים</a>
                        <a href="#offer" onClick={(e) => { e.preventDefault(); scrollToSection('offer'); }} className="lp-nav-link-btn">תקופת ניסיון בחינם</a>
                        <a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }} className="lp-nav-link-btn">שאלות נפוצות</a>
                        <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="lp-nav-link-btn">צור קשר</a>
                    </nav>

                    <div className="header-actions">
                        <a href="tel:0538201230" className="header-phone-link" title="חיוג טלפוני">
                            <FaPhoneAlt className="phone-icon" />
                            <span className="phone-number">053-820-1230</span>
                        </a>
                        <a 
                            href="/Login" 
                            className="btn-header-login"
                            onClick={(e) => { e.preventDefault(); navigate('/Login'); }}
                        >
                            <span>כניסה למערכת</span>
                            <FaArrowLeft className="arrow-icon" />
                        </a>
                    </div>
                </div>
            </header>

            {/* ── 2. Hero Section ── */}
            <section className="landing-hero-section">
                <div className="landing-container hero-container">
                    <div className="hero-content">
                        <div className="hero-trust-badge">
                            <FaShieldAlt className="trust-icon" />
                            <span>מערכת מתקדמת לניהול צי רכב ובטיחות בתעבורה</span>
                        </div>

                        <h1 className="hero-title">
                            מערכת דבורה <br />
                            <span className="title-gradient">ניהול צי רכב ובטיחות בתעבורה</span>
                        </h1>

                        <p className="hero-subtitle">
                            <strong>נמאס לכם מטבלאות אקסל?</strong> מערכת דבורה מנהלת עבורכם את צי הרכב: בקרת הוצאות דלק וליסינג, התראות על טסטים וביטוחים, תיק רכב דיגיטלי וחיסכון מוכח. תאמו הדגמה קצרה.
                        </p>

                        <div className="hero-cta-group">
                            <a 
                                href="/Login" 
                                className="btn-hero-primary"
                                onClick={(e) => { e.preventDefault(); navigate('/Login'); }}
                            >
                                <span>כניסה למערכת דבורה</span>
                                <FaArrowLeft className="btn-icon" />
                            </a>
                            <a 
                                href="#contact" 
                                className="btn-hero-secondary"
                                onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                            >
                                <span>תיאום הדגמה וייעוץ חינם</span>
                            </a>
                        </div>

                        <div className="hero-perks-row">
                            <div className="perk-item">
                                <FaCheckCircle className="perk-icon" />
                                <span>תקופת ניסיון מלאה בחינם</span>
                            </div>
                            <div className="perk-item">
                                <FaCheckCircle className="perk-icon" />
                                <span>ללא צורך בהתקנה מקומית</span>
                            </div>
                            <div className="perk-item">
                                <FaCheckCircle className="perk-icon" />
                                <span>גיבוי ענן מאובטח 24/7</span>
                            </div>
                        </div>
                    </div>

                    {/* Hero Live Mockup Showcase Card */}
                    <div className="hero-visual-card">
                        <div className="mockup-header-bar">
                            <div className="mockup-dots">
                                <span className="mockup-dot red"></span>
                                <span className="mockup-dot yellow"></span>
                                <span className="mockup-dot green"></span>
                            </div>
                            <div className="mockup-header-title">דבורה • תמונת מצב צי רכב בזמן אמת</div>
                        </div>

                        <div className="mockup-body">
                            {/* Live Vehicle Badge Preview */}
                            <div className="mockup-plate-row">
                                <div className="mockup-license-plate">
                                    <div className="mockup-plate-stripe">
                                        <span>IL</span>
                                        <span className="stripe-heb">ישראל</span>
                                    </div>
                                    <span className="mockup-plate-number">342-89-102</span>
                                </div>
                                <div className="mockup-status-badge active">
                                    <span className="dot"></span>
                                    <span>רכב פעיל • טסט בתוקף</span>
                                </div>
                            </div>

                            {/* Metrics chips */}
                            <div className="mockup-metrics-grid">
                                <div className="mockup-kpi-box">
                                    <span className="kpi-label">תוקף רישוי רכב</span>
                                    <span className="kpi-val valid">15/12/2026</span>
                                </div>
                                <div className="mockup-kpi-box">
                                    <span className="kpi-label">ביטוח חובה ומקיף</span>
                                    <span className="kpi-val valid">בתוקף</span>
                                </div>
                                <div className="mockup-kpi-box">
                                    <span className="kpi-label">הדרכת נהג תקופתית</span>
                                    <span className="kpi-val highlight">בוצעה</span>
                                </div>
                                <div className="mockup-kpi-box">
                                    <span className="kpi-label">בדיקת תקינות</span>
                                    <span className="kpi-val valid">100% מאושר</span>
                                </div>
                            </div>

                            {/* Activity strip */}
                            <div className="mockup-activity-strip">
                                <div className="activity-icon">
                                    <FaCalendarCheck />
                                </div>
                                <div className="activity-details">
                                    <span className="activity-text">בדיקת בטיחות חודשית נחתמה דיגיטלית</span>
                                    <span className="activity-date">לפני 15 דקות • קצין בטיחות</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 3. KPIs & Impact Stats Bar ── */}
            <section className="landing-stats-section">
                <div className="landing-container stats-grid">
                    <div className="stat-card">
                        <div className="stat-number">20+</div>
                        <div className="stat-title">חברות מנוהלות</div>
                        <div className="stat-desc">מעקב ציים, טיפולים ורישוי בזמן אמת</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">100%</div>
                        <div className="stat-title">שליטה ובקרה מלאה</div>
                        <div className="stat-desc">ניהול בטיחותי מקצועי ומניעת ליקויים</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">0</div>
                        <div className="stat-title">שימוש בניירת ואובדן מסמכים</div>
                        <div className="stat-desc">מעבר לטפסים וחתימות דיגיטליות בסמארטפון</div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-number">35%</div>
                        <div className="stat-title">חיסכון ממוצע בעלויות תפעול</div>
                        <div className="stat-desc">בקרה תקציבית, מניעת קנסות וניהול מונע</div>
                    </div>
                </div>
            </section>

            {/* ── 4. Core Capabilities & Features ── */}
            <section id="features" className="landing-features-section">
                <div className="landing-container">
                    <div className="section-header-center">
                        <span className="section-tag">יכולות המערכת</span>
                        <h2 className="section-title">כל מה שצי הרכב שלך צריך במערכת אחת</h2>
                        <p className="section-subtitle">
                            פתרון מקיף שתוכנן ונבנה בידי קציני בטיחות בתעבורה מנוסים, מותאם לצורכי חברות בעלות ציים קטנים, בינוניים וגדולים כאחד.
                        </p>
                    </div>

                    <div className="features-grid">
                        <div className="feature-card">
                            <div className="feature-icon-box car-icon">
                                <FaCar />
                            </div>
                            <h3 className="feature-title">תיק רכב דיגיטלי ממוחשב 360°</h3>
                            <p className="feature-text">
                                ניהול היסטוריה מלאה לכל רכב: רישוי שנתי, טסט, פוליסות ביטוח, טיפולים תקופתיים, קילומטראז׳, נסועה, תיעוד תקלות ותאונות.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon-box driver-icon">
                                <FaUserTie />
                            </div>
                            <h3 className="feature-title">תיק נהג דיגיטלי וקליטה</h3>
                            <p className="feature-text">
                                מעקב תוקף רישיונות נהיגה, הצהרות בריאות תקופתיות, בדיקות מידע תעבורתי, הדרכות בטיחות שנתיות ותיעוד שיוך כלי רכב.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon-box alert-icon">
                                <FaBell />
                            </div>
                            <h3 className="feature-title">התראות ותזכורות אוטומטיות</h3>
                            <p className="feature-text">
                                התראות חכמות לקצין הבטיחות ולנהגים לפני פקיעת תוקף של טסט, ביטוח, טיפול או רישיון נהיגה – למניעת השבתות וקנסות מיותרים.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon-box forms-icon">
                                <FaFileAlt />
                            </div>
                            <h3 className="feature-title">טפסים מקוונים וחתימה בסמארטפון</h3>
                            <p className="feature-text">
                                טופסי בדיקת תקינות רכב תקופתית, טופסי מסירה והחזרה, והצהרות נהג הנחתמים ישירות מהנייד ללא צורך בהדפסה או סריקה.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon-box reports-icon">
                                <FaChartBar />
                            </div>
                            <h3 className="feature-title">דוחות דינמיים וייצוא מהיר</h3>
                            <p className="feature-text">
                                הפקת דוחות ניהוליים מפורטים, ריכוז עלויות חודשיות ושנתיות, פילוח לפי מחלקות וייצוא מיידי בלחיצת כפתור לקובצי Excel ו-PDF.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon-box safety-icon">
                                <FaShieldAlt />
                            </div>
                            <h3 className="feature-title">ניהול בטיחות ומוכנות לביקורת</h3>
                            <p className="feature-text">
                                ריכוז כלל נתוני הבטיחות, תיעוד בדיקות תקופתיות והדרכות נהגים בצורה מסודרת ומוכנה לכל ביקורת בראש שקט.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 5. How It Works / Role-based Workflow ── */}
            <section id="how-it-works" className="landing-workflow-section">
                <div className="landing-container">
                    <div className="section-header-center">
                        <span className="section-tag">איך זה עובד?</span>
                        <h2 className="section-title">פתרון מותאם לכל בעל תפקיד בארגון</h2>
                        <p className="section-subtitle">
                            מערכת דבורה מחברת בין כל הגורמים המעורבים בניהול הצי ויוצרת סנכרון מושלם בזמן אמת.
                        </p>
                    </div>

                    <div className="workflow-tabs-bar">
                        <button 
                            type="button" 
                            className={`workflow-tab-btn ${activeRoleTab === 'officer' ? 'active' : ''}`}
                            onClick={() => setActiveRoleTab('officer')}
                        >
                            <FaShieldAlt className="tab-icon" />
                            <span>קצין בטיחות בתעבורה</span>
                        </button>
                        <button 
                            type="button" 
                            className={`workflow-tab-btn ${activeRoleTab === 'manager' ? 'active' : ''}`}
                            onClick={() => setActiveRoleTab('manager')}
                        >
                            <FaCogs className="tab-icon" />
                            <span>מנהל צי והנהלת החברה</span>
                        </button>
                        <button 
                            type="button" 
                            className={`workflow-tab-btn ${activeRoleTab === 'driver' ? 'active' : ''}`}
                            onClick={() => setActiveRoleTab('driver')}
                        >
                            <FaCar className="tab-icon" />
                            <span>נהגי החברה בשטח</span>
                        </button>
                    </div>

                    <div className="workflow-tab-content">
                        {activeRoleTab === 'officer' && (
                            <div className="role-showcase-box">
                                <div className="role-text-side">
                                    <h3 className="role-title">שליטה מקצועית ובקרה ללא פשרות</h3>
                                    <ul className="role-benefits-list">
                                        <li>מעקב מרכזי אחר כל ציי הלקוחות והחברות במסך מנהל מאוחד.</li>
                                        <li>תיעוד מלא של בדיקות תקינות תקופתיות והדרכות בטיחות לעמידה בחוק.</li>
                                        <li>מערך התראות למייל לפני פקיעת תוקף של מסמכים ורישיונות.</li>
                                        <li>הפקת דוחות רשמיים מוכנים להצגה למשרד התחבורה.</li>
                                    </ul>
                                </div>
                                <div className="role-highlight-box">
                                    <div className="highlight-pill">יתרון מרכזי לקצין בטיחות</div>
                                    <p className="highlight-quote">
                                        &ldquo;צמצום של שעות עבודה אדמיניסטרטיביות וסדר מופתי בכל תיקי הרכב והנהגים של לקוחותינו.&rdquo;
                                    </p>
                                    <span className="highlight-author">דבורה • משרד קציני בטיחות בתעבורה</span>
                                </div>
                            </div>
                        )}

                        {activeRoleTab === 'manager' && (
                            <div className="role-showcase-box">
                                <div className="role-text-side">
                                    <h3 className="role-title">שקיפות תקציבית ובקרה תפעולית</h3>
                                    <ul className="role-benefits-list">
                                        <li>מעקב בזמן אמת אחר כלל רכבי החברה, סטטוס פעיל מול מושבת, והקצאת נהגים.</li>
                                        <li>ריכוז עלויות חודשיות, הוצאות תחזוקה, טיפולים וביטוחים.</li>
                                        <li>ניהול הרשאות משתמשים מדויק (מנהל, עובד, צופה).</li>
                                        <li>מניעת קנסות וחידוש רישוי בזמן ללא השבתת פעילות עסקית.</li>
                                    </ul>
                                </div>
                                <div className="role-highlight-box">
                                    <div className="highlight-pill">חיסכון ישיר לארגון</div>
                                    <p className="highlight-quote">
                                        &ldquo;מניעת השבתות יקרות, ניהול מונע חכם, ושקט נפשי מלא בנוגע לתקינות הצי והנהגים.&rdquo;
                                    </p>
                                    <span className="highlight-author">בקרה ניהולית מתקדמת</span>
                                </div>
                            </div>
                        )}

                        {activeRoleTab === 'driver' && (
                            <div className="role-showcase-box">
                                <div className="role-text-side">
                                    <h3 className="role-title">חוויית שימוש נוחה ומהירה בשטח</h3>
                                    <ul className="role-benefits-list">
                                        <li>מילוי טופסי בדיקה חודשיים ישירות ממכשיר הסמארטפון ללא צורך בהדפסת נייר.</li>
                                        <li>חתימה דיגיטלית קלה ומאובטחת על גבי מסך המגע.</li>
                                        <li>דיווח מהיר על תקלות, תאונות או אירועים ישירות לקצין הבטיחות.</li>
                                        <li>שקיפות מלאה לגבי תוקף הרישיון האישי וההדרכות הנדרשות.</li>
                                    </ul>
                                </div>
                                <div className="role-highlight-box">
                                    <div className="highlight-pill">פשטות תפעולית</div>
                                    <p className="highlight-quote">
                                        &ldquo;לחיצה קצרה על קישור בסלולר, מילוי מהיר, חתימה באצבע – והטופס כבר מתוייק בתיק הרכב.&rdquo;
                                    </p>
                                    <span className="highlight-author">חוויית נהג דיגיטלית</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* ── 6. Multi-Device & Mobile Ready Showcase ── */}
            <section id="devices" className="landing-devices-section">
                <div className="landing-container devices-container">
                    <div className="devices-text-col">
                        <div className="devices-tag">
                            <FaCloud className="cloud-icon" />
                            <span>ענן מאובטח ונגיש</span>
                        </div>
                        <h2 className="devices-title">כנסו למערכת מכל מכשיר ונהלו את צי הרכב</h2>
                        <p className="devices-desc">
                            מערכת דבורה מותאמת באופן מושלם לכל גודל מסך. בין אם אתם יושבים מול מסך רחב במשרד, עובדים מטאבלט במוסך, או חותמים על טופס בדיקה בסמארטפון לצד הרכב – המערכת תמיד זמינה, מהירה ואינטואיטיבית.
                        </p>
                        <div className="devices-features-list">
                            <div className="device-item">
                                <FaLaptop className="d-icon" />
                                <div>
                                    <h4>מחשב שולחני ונייד</h4>
                                    <p>ניהול מלא, דוחות מפורטים וטבלאות נתונים מתקדמות</p>
                                </div>
                            </div>
                            <div className="device-item">
                                <FaMobileAlt className="d-icon" />
                                <div>
                                    <h4>סמארטפון וטאבלט</h4>
                                    <p>טפסים מקוונים, חתימות דיגיטליות וסריקת מסמכים בשטח</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="devices-visual-col">
                        <div className="device-preview-card">
                            <div className="preview-header">
                                <span className="p-dot"></span>
                                <span className="p-title">טופס בדיקת רכב דיגיטלי בסמארטפון</span>
                            </div>
                            <div className="preview-content">
                                <div className="p-row">
                                    <span>בלמים והיגוי:</span>
                                    <span className="p-badge success">תקין</span>
                                </div>
                                <div className="p-row">
                                    <span>צמיגים ולחץ אוויר:</span>
                                    <span className="p-badge success">תקין</span>
                                </div>
                                <div className="p-row">
                                    <span>אורות ואיתות:</span>
                                    <span className="p-badge success">תקין</span>
                                </div>
                                <div className="p-row">
                                    <span>ציוד בטיחות ואפוד:</span>
                                    <span className="p-badge success">תקין</span>
                                </div>
                                <div className="p-signature-area">
                                    <span className="sig-label">חתימת הנהג הדיגיטלית:</span>
                                    <div className="sig-box">
                                        <span className="sig-font">נחתם דיגיטלית ✓</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 7. Offer / Free Trial Banner ── */}
            <section id="offer" className="landing-offer-section">
                <div className="landing-container">
                    <div className="offer-banner-card">
                        <div className="offer-content">
                            <span className="offer-badge">הצעה מיוחדת לזמן ממושך</span>
                            <h2 className="offer-title">קבלו תקופת ניסיון מלאה בחינם!</h2>
                            <p className="offer-desc">
                                כרגע המערכת ניתנת בחינם לגמרי לעוד הרבה זמן – ללא שום עלות, ללא התחייבות, ועם ליווי אישי והטמעה מלאה של כל צי הרכב והנהגים שלכם.
                            </p>
                            <div className="offer-actions">
                                <a href="tel:0538201230" className="btn-offer-phone">
                                    <FaPhoneAlt />
                                    <span>חייגו: 053-820-1230</span>
                                </a>
                                <a 
                                    href="#contact" 
                                    className="btn-offer-form"
                                    onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
                                >
                                    <span>השאירו פרטים ונחזור אליכם</span>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 7.5. FAQ & SEO Rich Questions Section ── */}
            <section id="faq" className="landing-faq-section">
                <div className="landing-container">
                    <div className="section-header-center faq-header">
                        <span className="section-tag">שאלות נפוצות</span>
                        <h2 className="section-title">כל מה שחשוב לדעת על מערכת דבורה וניהול צי רכב</h2>
                        <p className="section-subtitle">
                            תשובות לשאלות הנפוצות ביותר אודות מערכת דבורה, יתרונותיה לקציני בטיחות בתעבורה והתאמתה לארגון שלכם.
                        </p>
                    </div>

                    <div className="faq-accordion-list">
                        {FAQ_ITEMS.map((item, idx) => {
                            const isOpen = openFaqIndices.includes(idx);
                            return (
                                <div key={idx} className={`faq-item ${isOpen ? 'active' : ''}`}>
                                    <button 
                                        type="button" 
                                        className="faq-question-btn"
                                        onClick={() => toggleFaq(idx)}
                                        aria-expanded={isOpen}
                                    >
                                        <div className="faq-q-text">
                                            <FaQuestionCircle className="faq-q-icon" />
                                            <span>{item.q}</span>
                                        </div>
                                        <FaChevronDown className="faq-toggle-arrow" />
                                    </button>
                                    {isOpen && (
                                        <div className="faq-answer-box">
                                            <p>{item.a}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ── 8. Contact & Lead Capture Section ── */}
            <section id="contact" className="landing-contact-section">
                <div className="landing-container contact-container">
                    <div className="contact-info-col">
                        <span className="section-tag">צרו איתנו קשר</span>
                        <h2 className="contact-title">לכל שאלה, ייעוץ או תיאום הדגמה חיה</h2>
                        <p className="contact-desc">
                            צוות המומחים וקציני הבטיחות עומד לרשותכם בכל שאלה לגבי ניהול צי הרכב, עמידה בתקנות או התאמת המערכת לצרכים הייחודיים שלכם.
                        </p>

                        <div className="contact-details-list">
                            <div className="contact-item">
                                <div className="c-icon-box">
                                    <FaPhoneAlt />
                                </div>
                                <div>
                                    <span className="c-label">טלפון:</span>
                                    <a href="tel:0538201230" className="c-val">053-820-1230</a>
                                </div>
                            </div>

                            <div className="contact-item">
                                <div className="c-icon-box">
                                    <FaEnvelope />
                                </div>
                                <div>
                                    <span className="c-label">דואר אלקטרוני:</span>
                                    <a href="mailto:Dvorabm23@gmail.com" className="c-val">Dvorabm23@gmail.com</a>
                                </div>
                            </div>
                        </div>

                        <a 
                            href="https://api.whatsapp.com/send?phone=972538201230&text=%D7%A9%D7%9C%D7%95%D7%9D%2C%20%D7%90%D7%A0%D7%99%20%D7%9E%D7%A2%D7%95%D7%A0%D7%99%D7%9F%20%D7%91%D7%A4%D7%A8%D7%98%D7%99%D7%9D%20%D7%A2%D7%9C%20%D7%9E%D7%A2%D7%A8%D7%9F%D7%AA%20%D7%93%D7%91%D7%95%D7%A8%D7%94%20%D7%9C%D7%A0%D7%99%D7%94%D7%95%D7%9C%20%D7%A6%D7%99%20%D7%A8%D7%9B%D7%91" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-whatsapp-direct"
                        >
                            <FaWhatsapp className="wa-icon" />
                            <span>פנייה ישירה ב-WhatsApp</span>
                        </a>
                    </div>

                    <div className="contact-form-col">
                        <div className="form-card">
                            <h3 className="form-header-title">השאירו פרטים ונחזור אליכם בהקדם</h3>
                            <p className="form-header-subtitle">נשמח לענות על כל שאלה ולהציג לכם הדגמה ללא התחייבות</p>

                            {formSubmitted ? (
                                <div className="form-success-banner">
                                    <FaCheckCircle className="success-icon" />
                                    <h4>תודה על פנייתך!</h4>
                                    <p>הפרטים התקבלו בהצלחה ונשלחו לצוות דבורה. נציג מקצועי ייצור איתך קשר בהקדם.</p>
                                    <button 
                                        type="button" 
                                        className="btn-reset-form"
                                        onClick={() => {
                                            setFormSubmitted(false);
                                            setSubmitError('');
                                        }}
                                    >
                                        שליחת פנייה נוספת
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleFormSubmit} className="landing-contact-form">
                                    {submitError && (
                                        <div className="form-error-banner" role="alert">
                                            {submitError}
                                        </div>
                                    )}
                                    <div className="landing-input-group">
                                        <label htmlFor="landing-fullName">שם מלא *</label>
                                        <input 
                                            id="landing-fullName"
                                            type="text" 
                                            required 
                                            placeholder="הכנס שם מלא"
                                            value={formData.fullName}
                                            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div className="landing-input-group">
                                        <label htmlFor="landing-phone">מספר טלפון *</label>
                                        <input 
                                            id="landing-phone"
                                            type="tel" 
                                            required 
                                            placeholder="05X-XXXXXXX"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                            disabled={isSubmitting}
                                        />
                                    </div>


                                    <div className="landing-input-group">
                                        <label htmlFor="landing-fleetSize">גודל צי הרכב המוערך</label>
                                        <select 
                                            id="landing-fleetSize"
                                            value={formData.fleetSize}
                                            onChange={(e) => setFormData({ ...formData, fleetSize: e.target.value })}
                                            disabled={isSubmitting}
                                        >
                                            <option value="1-5">1-5 כלי רכב</option>
                                            <option value="6-20">6-20 כלי רכב</option>
                                            <option value="21-50">21-50 כלי רכב</option>
                                            <option value="50+">מעל 50 כלי רכב</option>
                                        </select>
                                    </div>

                                    <div className="landing-input-group">
                                        <label htmlFor="landing-notes">הערות או בקשות מיוחדות</label>
                                        <textarea 
                                            id="landing-notes"
                                            rows="3" 
                                            placeholder="נשמח לשמוע על סוג הפעילות או שאלות שיש לכם..."
                                            value={formData.notes}
                                            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                            disabled={isSubmitting}
                                        ></textarea>
                                    </div>

                                    <button 
                                        type="submit" 
                                        className="btn-submit-lead"
                                        disabled={isSubmitting}
                                    >
                                        <span>{isSubmitting ? 'שולח פרטים...' : 'תחזרו אליי עם פרטים והדגמה'}</span>
                                        <FaArrowLeft />
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── 9. Official Footer ── */}
            <footer className="landing-footer">
                <div className="landing-container footer-container">
                    <div className="footer-brand-col">
                        <div className="footer-logo">
                            <img src={websiteLogo} alt="דבורה" className="landing-footer-logo-img" width="140" height="44" loading="lazy" />
                            <span className="logo-title">דבורה</span>
                        </div>
                        <p className="footer-tagline">
                            מערכת ענן מקצועית לניהול צי רכב וקציני בטיחות בתעבורה.
                        </p>
                    </div>

                    <div className="footer-links-col">
                        <h4>קישורים ומידע</h4>
                        <ul>
                            <li><a href="#features" onClick={(e) => { e.preventDefault(); scrollToSection('features'); }}>יתרונות המערכת</a></li>
                            <li><a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}>איך זה עובד</a></li>
                            <li><a href="#offer" onClick={(e) => { e.preventDefault(); scrollToSection('offer'); }}>תקופת ניסיון מלאה בחינם</a></li>
                            <li><a href="#faq" onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}>שאלות נפוצות (FAQ)</a></li>
                            <li><Link to="/accessibility">הצהרת נגישות</Link></li>
                            <li><Link to="/terms">תנאי שימוש והגבלת אחריות</Link></li>
                            <li><Link to="/privacy">מדיניות פרטיות</Link></li>
                            <li><Link to="/Login">כניסה למערכת דבורה</Link></li>
                        </ul>
                    </div>

                    <div className="footer-contact-col">
                        <h4>דבורה מערכות</h4>
                        <p>משרד קציני בטיחות בתעבורה</p>
                        <p>טלפון: <a href="tel:0538201230">053-820-1230</a></p>
                        <p>דוא״ל: <a href="mailto:Dvorabm23@gmail.com">Dvorabm23@gmail.com</a></p>
                    </div>
                </div>

                <div className="footer-bottom-bar">
                    <div className="landing-container bottom-flex">
                        <span>© {new Date().getFullYear()} כל הזכויות שמורות למערכת &apos;דבורה&apos;</span>
                        <div className="bottom-links">
                            <Link to="/terms">תנאי שימוש</Link>
                            <span>•</span>
                            <Link to="/privacy">מדיניות פרטיות</Link>
                            <span>•</span>
                            <Link to="/accessibility">הצהרת נגישות</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
